import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import 'temporal-polyfill/global';
import './generated/componentImports.js';
import './generated/shoelaceImports.js';

import { Task, TaskStatus } from '@lit/task';
import { registerIconLibrary } from '@shoelace-style/shoelace/dist/utilities/icon-library.js';
import ms from 'ms';
import { api } from './api.js';
import { contexts, ContextTask, FKUserChanged, provide } from './contexts.js';
import { PageAdministrationAudit } from './page-administration-audit.js';
import { PageAdministrationBilling } from './page-administration-billing.js';
import { PageAdministrationQueues } from './page-administration-queues.js';
import { PageAdministrationUsage } from './page-administration-usage.js';
import { PageAdministrationUsers } from './page-administration-users.js';
import { PageCommit } from './page-commit.js';
import { PageDeviceAuthentication } from './page-device-authentication.js';
import { PageLogin } from './page-login.js';
import { PageNewOrganization } from './page-new-organization.js';
import { PageNewProject } from './page-new-project.js';
import { PageOrganizationAudit } from './page-organization-audit.js';
import { PageOrganizationBilling } from './page-organization-billing.js';
import { PageOrganizationMembers } from './page-organization-members.js';
import { PageOrganizationSettings } from './page-organization-settings.js';
import { PageOrganizationUsage } from './page-organization-usage.js';
import { PageOrganization } from './page-organization.js';
import { PageProjectAnalytics } from './page-project-analytics.js';
import { PageProjectAccess } from './page-project-access.js';
import { PageProjectHistory } from './page-project-history.js';
import { PageProjectPulls } from './page-project-pulls.js';
import { PageProjectRuns } from './page-project-runs.js';
import { PageProjectSettings } from './page-project-settings.js';
import { PageProjectSuperadm } from './page-project-superadm.js';
import { PageProjectTests } from './page-project-tests.js';
import { PageProjectUsage } from './page-project-usage.js';
import { PagePull } from './page-pull.js';
import { PageReportViewerLanding } from './page-report-viewer-landing.js';
import { PageReportViewer } from './page-report-viewer.js';
import { PageRun } from './page-run.js';
import { PageUserOrganizations } from './page-user-organizations.js';
import { PageUserProjects } from './page-user-projects.js';
import { PageUserSettings } from './page-user-settings.js';
import { SNAPSHOT_ID_PARAM } from './platformUrl.js';
import { Router } from './router.js';

registerIconLibrary('boxicons', {
  resolver: name => {
    let folder = 'regular';
    if (name.substring(0, 4) === 'bxs-') folder = 'solid';
    if (name.substring(0, 4) === 'bxl-') folder = 'logos';
    return `/assets/boxicons/${folder}/${name}.svg`;
  },
  mutator: svg => svg.setAttribute('fill', 'currentColor')
});

@customElement('the-app')
export class App extends LitElement {

  @provide({ context: contexts.notLoggedIn }) private _notLoggedIn: boolean = false;

  private _userTask = new ContextTask(this, contexts.user, {
    args: () => [],
    task: async ([], { signal }) => {
      return await api.user.whoami.GET(undefined, { signal }).then(user => {
        this._notLoggedIn = false;
        return user;
      }).catch(e => {
        this._notLoggedIn = true;
        return undefined;
      });
    }
  });

  private _serverInfoTask = new ContextTask(this, contexts.serverInfo, {
    args: () => [],
    task: async ([], { signal }) => {
      return await api.serverInfo.GET(undefined, { signal });
    }
  });

  // Fetched here rather than in <user-header>: every dashboard page renders its
  // own header, so a per-header fetch made the badge pop in on each navigation.
  private _auditLogRecentCountTask = new ContextTask(this, contexts.auditLogRecentCount, {
    args: () => [this._userTask.value?.isSuperUser] as const,
    task: async ([isSuperUser], { signal }) => {
      if (!isSuperUser)
        return undefined;
      const { count } = await api.administration.auditLogRecentCount.GET(undefined, { signal });
      return count;
    }
  });

  private _serverInfoInterval?: NodeJS.Timeout;

  // Server info and the audit-log count are fetched once at startup, but
  // superusers see live server stats (uptime, RAM, snapshots) in the footer and
  // the audit-log badge in the dashboard header, so keep both fresh for them.
  private _serverInfoRefreshTimerTask = new Task(this, {
    args: () => [this._userTask.value] as const,
    task: async ([user], { signal }) => {
      clearInterval(this._serverInfoInterval);
      if (!user?.isSuperUser)
        return;
      this._serverInfoInterval = setInterval(() => {
        this._serverInfoTask.run();
        this._auditLogRecentCountTask.run();
      }, ms('1 minute'));
    }
  });

  @provide({ context: contexts.router })
  private _router: Router = new Router();

  @property({ attribute: 'report-viewer', type: Boolean }) reportViewer?: string;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this._serverInfoInterval);
  }

  connectedCallback() {
    super.connectedCallback();
    // The user context is fetched once with no arguments, so editing the
    // profile has to ask for it explicitly.
    this.addEventListener(FKUserChanged.eventName, () => this._userTask.run());
    this._router.ignoreClientSideNavigation('/login/*');
    this._router.ignoreClientSideNavigation('/logout/*');
    this._router.ignoreClientSideNavigation('/*/billing/checkout');
    this._router.ignoreClientSideNavigation('/api/run/playwrightTrace/*');
    // In-app navigations stay within the loaded snapshot anyway; only new tabs need it.
    this._router.ignoreSearchParam(SNAPSHOT_ID_PARAM);
    this._router.setNotFoundRenderer(() => html`<page-http-error code=404></page-http-error>`);

    const loggedInUser = this._router.withMiddleware(() => {
      if (!this._userTask.value && this._userTask.status !== TaskStatus.PENDING) {
        return html`<page-login></page-login>`;
      }
    });

    const anonUser = this._router.withMiddleware(() => {
      return undefined;
    });
    anonUser.allRoutes(PageLogin.routes());

    if (this.reportViewer) {
      // Organize immediate inplace redirect to the viewer, if we're loading
      // landing page with token & port.
      const url = new URL(window.location.href);
      const hasTokenPort = url.searchParams.has('token') && url.searchParams.has('port');
      const hasReportUrl = url.searchParams.has('url');
      if (url.pathname === '/' && (hasTokenPort || hasReportUrl)) {
        url.pathname = '/viewer';
        window.history.replaceState({}, '', url.toString());
      }
      anonUser.allRoutes(PageReportViewerLanding.routes());
      anonUser.allRoutes(PageReportViewer.routes());
    } else {
      this._router.ignoreClientSideNavigation('/viewer.html');
      loggedInUser.allRoutes(PageAdministrationAudit.routes());
      loggedInUser.allRoutes(PageAdministrationBilling.routes());
      loggedInUser.allRoutes(PageAdministrationQueues.routes());
      loggedInUser.allRoutes(PageAdministrationUsage.routes());
      loggedInUser.allRoutes(PageAdministrationUsers.routes());
      loggedInUser.allRoutes(PageOrganizationSettings.routes());
      loggedInUser.allRoutes(PageOrganizationUsage.routes());
      loggedInUser.allRoutes(PageOrganizationMembers.routes());
      loggedInUser.allRoutes(PageOrganizationAudit.routes());
      loggedInUser.allRoutes(PageOrganizationBilling.routes());
      loggedInUser.allRoutes(PageNewProject.routes());
      loggedInUser.allRoutes(PageNewOrganization.routes());
      loggedInUser.allRoutes(PageUserProjects.routes());
      loggedInUser.allRoutes(PageUserOrganizations.routes());
      loggedInUser.allRoutes(PageUserSettings.routes());
      loggedInUser.allRoutes(PageDeviceAuthentication.routes());
      loggedInUser.allRoutes(PageProjectSuperadm.routes());
      anonUser.allRoutes(PagePull.routes());
      anonUser.allRoutes(PageCommit.routes());
      anonUser.allRoutes(PageProjectTests.routes());
      anonUser.allRoutes(PageProjectSettings.routes());
      anonUser.allRoutes(PageProjectRuns.routes());
      anonUser.allRoutes(PageProjectPulls.routes());
      anonUser.allRoutes(PageProjectHistory.routes());
      anonUser.allRoutes(PageProjectAnalytics.routes());
      anonUser.allRoutes(PageProjectAccess.routes());
      anonUser.allRoutes(PageProjectUsage.routes());
      anonUser.allRoutes(PageRun.routes());
      anonUser.allRoutes(PageOrganization.routes());
    }
  }

  render() {
    return this._userTask.render({
      // Keep the routed UI mounted while revalidating an existing user. Only
      // the initial whoami request needs to gate rendering.
      pending: () => this._userTask.value ? this._router : html``,
      error: () => html`Failed to fetch user`,
      complete: () => this._router,
    })
  }

  static styles = [css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100vh;
    }
  `];
}
